import type { ReactNode } from 'react';
import { Icon } from '@/components/Icon';
import { cueAt, type TutorialStep } from './tutorialsModel';

interface StageProps {
  step: TutorialStep;
  playing: boolean;
  progress: number;
  finished: boolean;
  /** Autoplay was refused by the browser: the play control says one tap starts the lesson. */
  blocked: boolean;
  captions: boolean;
  onToggle: () => void;
  /** Covers the stage once the lesson ended (LessonEnd: countdown or completion card). */
  children?: ReactNode;
}

/** The step's screenshot, letterboxed in a fixed 16:9 box; a quiet placeholder when the step has none. */
function StepImage({ step }: { step: TutorialStep }) {
  if (!step.image) {
    return (
      <div className="flex size-full flex-col items-center justify-center gap-2 p-6 text-center text-subtle">
        <Icon name="image" size={32} />
        <p className="max-w-[40ch] text-sm">This step has no screenshot yet; its narration is below.</p>
      </div>
    );
  }
  return <img key={step.key} src={step.image} alt={step.title} className="fade-in size-full object-contain" />;
}

/** One caption cue at a time, advancing with the narration. */
function Caption({ step, progress }: { step: TutorialStep; progress: number }) {
  const index = cueAt(step.cues, progress);
  const cue = step.cues[index];
  if (!cue) return null;
  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-0 p-2 sm:p-4">
      <p
        key={`${step.key}-${index}`}
        data-testid="lesson-caption"
        aria-live="polite"
        className="fade-in mx-auto w-fit max-w-[60ch] rounded-md bg-background/85 px-3 py-1.5 text-center text-[13px] font-medium leading-snug text-foreground shadow-card backdrop-blur sm:text-[15px]"
      >
        {cue}
      </p>
    </div>
  );
}

/** The large play control while paused; when autoplay was refused it also says so, so the lesson never stalls silently. */
function PlayButton({ blocked, onToggle }: { blocked: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={blocked ? 'Play (your browser held back autoplay)' : 'Play'}
      data-testid="lesson-stage-play"
      data-blocked={blocked || undefined}
      className="group absolute inset-0 flex flex-col items-center justify-center gap-2 outline-none"
    >
      <span className="flex size-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lift transition-transform duration-150 ease-out group-hover:scale-105 group-focus-visible:ring-[3px] group-focus-visible:ring-ring/50 motion-reduce:transition-none sm:size-16">
        <Icon name="play_arrow" size={32} fill />
      </span>
      {blocked ? (
        <span data-testid="lesson-autoplay-blocked" className="rounded-md bg-background/85 px-2.5 py-1 text-[13px] font-medium text-foreground shadow-card backdrop-blur">
          Your browser held back autoplay. Press play.
        </span>
      ) : null}
    </button>
  );
}

/** The stage: screenshot, caption, a large play control while paused, the lesson end (children) once it ended. */
export function PlayerStage({ step, playing, progress, finished, blocked, captions, onToggle, children }: StageProps) {
  return (
    <div data-testid="lesson-stage" data-playing={playing || undefined} className="relative aspect-video w-full overflow-hidden rounded-lg border bg-secondary shadow-card">
      <StepImage step={step} />
      {captions && !finished && !blocked ? <Caption step={step} progress={progress} /> : null}
      {!playing && !finished ? <PlayButton blocked={blocked} onToggle={onToggle} /> : null}
      {finished ? children : null}
    </div>
  );
}
